/**
 * Drag-and-drop photo attachment grid for a batch.
 * Thumbnails are downscaled client-side before upload to save bandwidth.
 */

import { useEffect, useState, type DragEvent, type ChangeEvent } from 'react';
import * as attachApi from '../api/attachments';
import type { Attachment } from '../types';
import { useToast } from '../hooks/useToast';

const MAX_DIM = 1600;
const MAX_BYTES = 10 * 1024 * 1024;

interface Props {
  batchId: string | null;
}

export default function AttachmentsCard({ batchId }: Props) {
  const [items, setItems] = useState<Attachment[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const { show } = useToast();

  useEffect(() => {
    if (!batchId) {
      setItems([]);
      return;
    }
    let cancelled = false;
    attachApi
      .listAttachments(batchId)
      .then(({ items }) => {
        if (!cancelled) setItems(items);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [batchId]);

  async function ingest(files: FileList | File[]) {
    if (!batchId) return;
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) {
          show(`Skipped: ${file.name} (not an image)`);
          continue;
        }
        const compressed = await downscaleImage(file).catch(() => file);
        if (compressed.size > MAX_BYTES) {
          show(`Skipped: ${file.name} (too large)`);
          continue;
        }
        try {
          const added = await attachApi.uploadAttachment(batchId, compressed);
          setItems((cur) => [added, ...cur]);
        } catch (err) {
          const msg = (err as { message?: string })?.message ?? 'upload failed';
          show(`Failed: ${file.name} — ${msg}`);
        }
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this attachment?')) return;
    await attachApi.deleteAttachment(id);
    setItems((cur) => cur.filter((x) => x.id !== id));
  }

  function onDrop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setDragOver(false);
    if (!batchId) return;
    if (e.dataTransfer.files.length > 0) ingest(e.dataTransfer.files);
  }

  function onPicked(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files) ingest(e.target.files);
    e.target.value = '';
  }

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">Photos</span>
        <span style={{ fontSize: 11, color: 'var(--ink3)' }}>
          {items.length} attached
        </span>
      </div>
      <div className="card-body">
        <label
          className={'attach-dropzone' + (dragOver ? ' dragover' : '')}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            onChange={onPicked}
            style={{ display: 'none' }}
          />
          {busy ? 'Uploading…' : 'Drop images here or click to upload'}
        </label>
        {items.length > 0 && (
          <div className="attach-grid" style={{ marginTop: 12 }}>
            {items.map((a) => (
              <div key={a.id} className="attach-tile">
                <img src={a.url} alt={a.filename} loading="lazy" />
                <button
                  className="attach-x"
                  onClick={() => handleDelete(a.id)}
                  aria-label="Delete"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

async function downscaleImage(file: File): Promise<File> {
  if (file.size < 800 * 1024) return file; // already small
  const bmp = await createImageBitmap(file).catch(() => null);
  if (!bmp) return file;
  const scale = Math.min(1, MAX_DIM / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * scale);
  const h = Math.round(bmp.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(bmp, 0, 0, w, h);
  const blob: Blob | null = await new Promise((res) =>
    canvas.toBlob((b) => res(b), 'image/jpeg', 0.85),
  );
  if (!blob) return file;
  return new File([blob], file.name.replace(/\.\w+$/, '.jpg'), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}
